import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from 'components/ui/badge';
import dayjs from 'dayjs';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import ApiQuestionnaireManager from 'services/api/ApiQuestionnaireManager';
import { QuestionnaireResponseDTO, QuestionnaireSchema } from 'services/api/models/QuestionnaireDTO';

type Props = {
  response: QuestionnaireResponseDTO;
  schema: QuestionnaireSchema;
  /** When provided the star toggle is shown and highlights can be edited. */
  responseId?: number;
};

function resolveDisplayValue(
  q: QuestionnaireSchema['sections'][number]['questions'][number],
  answer: QuestionnaireResponseDTO['answers'][string],
  answers: QuestionnaireResponseDTO['answers'],
  t: ReturnType<typeof useTranslation>['t'],
): { displayValue: string; isEmpty: boolean } {
  const isEmpty =
    answer === undefined || answer === null || answer === '' || (Array.isArray(answer) && answer.length === 0);

  let displayValue: string;
  if (isEmpty) {
    displayValue = '—';
  } else if (Array.isArray(answer)) {
    displayValue = answer
      .map(v => {
        const choice = q.choices?.find(c => c.value === v);
        return choice ? choice.label : v;
      })
      .join(', ');
  } else if (q.type === 'select_year') {
    const year = Number(answer);
    const age = year > 0 && !isNaN(year) ? dayjs().year() - year : null;
    displayValue = age !== null ? `${answer} (${age} ans)` : String(answer);
  } else if (q.type === 'boolean') {
    displayValue = answer ? t('medicalProfile.questionnaire.yes') : t('medicalProfile.questionnaire.no');
  } else if (q.choices) {
    const choice = q.choices.find(c => c.value === answer);
    displayValue = choice ? choice.label : String(answer);
  } else {
    displayValue = String(answer);
  }

  if (q.id === 'weight_kg') {
    const heightCm = Number(answers['height_cm']);
    const weightKg = Number(answer);
    if (heightCm > 0 && weightKg > 0) {
      const heightM = heightCm / 100;
      const bmi = (weightKg / (heightM * heightM)).toFixed(1);
      displayValue = `${displayValue} — IMC : ${bmi}`;
    }
  }

  return { displayValue, isEmpty };
}

function QuestionRow({
  q,
  displayValue,
  isEmpty,
  isHighlighted,
  context = 'schema',
  onToggle,
}: {
  q: { id: string; label: string };
  displayValue: string;
  isEmpty: boolean;
  isHighlighted: boolean;
  /** 'highlight-list' = inside the highlights summary box; 'schema' = inside the full schema list */
  context?: 'highlight-list' | 'schema';
  onToggle?: () => void;
}) {
  const toggleIcon =
    context === 'highlight-list' ? (
      <MinusCircleOutlined />
    ) : isHighlighted ? (
      <ExclamationCircleFilled />
    ) : (
      <ExclamationCircleOutlined />
    );

  // The "average" severity yellow marks a highlighted question; the rest stay recessive.
  const toggleColorClassName =
    context === 'highlight-list'
      ? 'text-subtle-foreground'
      : isHighlighted
        ? 'text-chart-severity-average'
        : 'text-subtle-foreground';

  const toggleAriaLabel =
    context === 'highlight-list' || isHighlighted ? 'Retirer des points à retenir' : 'Mettre en avant';

  return (
    <div className="mb-2.5 flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <span className="block text-sm text-subtle-foreground">{q.label}</span>
        <span className={cn('font-medium', isEmpty && 'text-subtle-foreground')}>{displayValue}</span>
      </div>
      {onToggle && (
        <button
          type="button"
          aria-label={toggleAriaLabel}
          onClick={onToggle}
          className={cn(
            'mt-0.5 shrink-0 cursor-pointer border-0 bg-transparent px-1 py-0.5 text-base leading-none transition-colors duration-150',
            toggleColorClassName,
          )}
        >
          {toggleIcon}
        </button>
      )}
    </div>
  );
}

export default function QuestionnaireResponseViewer({ response, schema, responseId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: toggleHighlight } = useMutation({
    mutationFn: (questionId: string) => ApiQuestionnaireManager.toggleHighlight(responseId!, questionId),
    onSuccess: updatedIds => {
      queryClient.setQueryData<QuestionnaireResponseDTO | null>(
        ['questionnaire', 'responseForPatient', responseId],
        old => (old ? { ...old, highlighted_question_ids: updatedIds } : old),
      );
    },
  });

  // highlightedIds is derived entirely from the query cache (via the response prop).
  // The mutation's onSuccess patches the cache, which flows back here through the parent.
  const highlightedIds = response.highlighted_question_ids ?? [];

  const highlightedQuestions: Array<{
    q: QuestionnaireSchema['sections'][number]['questions'][number];
    displayValue: string;
    isEmpty: boolean;
  }> = [];

  for (const section of schema.sections) {
    for (const q of section.questions) {
      if (q.type === 'display' || q.type === 'separator' || q.type === 'info') continue;
      if (!highlightedIds.includes(q.id)) continue;
      const answer = response.answers[q.id];
      const resolved = resolveDisplayValue(q, answer, response.answers, t);
      highlightedQuestions.push({ q, ...resolved });
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-content flex-col gap-6">
      {response.reviewed_at && (
        <div className="mt-4 flex w-full justify-center gap-2">
          <Badge variant="secondary" className="self-start">
            <CheckCircleFilled className="mr-2 text-foreground" />
            {t('medicalProfile.caregiver.reviewedBy', {
              date: dayjs(response.reviewed_at).format('DD/MM/YYYY'),
            })}
          </Badge>
        </div>
      )}

      {highlightedQuestions.length > 0 && (
        <div className="rounded-md border border-info-muted bg-success-muted px-5 py-4">
          <div className="mb-3 flex items-center gap-2">
            <ExclamationCircleFilled className="text-base text-chart-severity-average" />
            <span className="text-sm font-semibold text-muted-foreground">
              {t('medicalProfile.caregiver.highlights.sectionTitle')}
            </span>
          </div>
          {highlightedQuestions.map(({ q, displayValue, isEmpty }) => (
            <QuestionRow
              key={q.id}
              q={q}
              displayValue={displayValue}
              isEmpty={isEmpty}
              isHighlighted
              context="highlight-list"
              onToggle={responseId !== undefined ? () => toggleHighlight(q.id) : undefined}
            />
          ))}
        </div>
      )}

      {schema.sections.map(section => (
        <div key={section.id}>
          <h2 className="my-8 border-t border-info-muted pt-4 text-foreground">{section.label}</h2>
          {section.questions.map(q => {
            // Info alerts are patient-facing guidance only, not part of the response
            if (q.type === 'info') return null;
            if (q.type === 'display')
              return (
                <span key={q.id} className="mb-2 block text-sm text-subtle-foreground italic">
                  {q.label}
                </span>
              );
            if (q.type === 'separator') return <div key={q.id} className="my-4 border-t border-background" />;

            const answer = response.answers[q.id];
            const { displayValue, isEmpty } = resolveDisplayValue(q, answer, response.answers, t);

            return (
              <QuestionRow
                key={q.id}
                q={q}
                displayValue={displayValue}
                isEmpty={isEmpty}
                isHighlighted={highlightedIds.includes(q.id)}
                onToggle={responseId !== undefined ? () => toggleHighlight(q.id) : undefined}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
